import type { Branch, ObjectId, Progress, ResolvedState, Track, TrackStep } from '@/api/types'

/**
 * El track se deriva en runtime del catálogo de estados, parseando el prefijo
 * ordinal de `name.display` (spec §4). No se mantiene una lista de estados a
 * mano en el código: si Cotalker agrega un estado, aparece solo.
 *
 *   "0. En revisión: Jefe de Zona"  → mayor 0            → un círculo
 *   "3.2. En Revisión Presupuesto"  → mayor 3, menor 2   → rama del círculo 3
 */
const ORDINAL = /^(\d+)(?:\.(\d+))?\.?\s*/

export interface CatalogEntry {
  code: string
  display: string
  propertyId: ObjectId
  /** `_id` de la entrada de `/sm/smstate`. Es con lo que filtra la API. */
  smStateId?: ObjectId
}

/** Estados que no se dibujan como paso del track. */
const TRACK_EXCLUDED = new Set([
  'incidents_rejected', // 5. — fuera del listado, no es un paso del camino feliz
  'ot_cancelled', // 8. — idem
  'ot_checklist_pending', // 1.2 deprecado
  'ot_checklists_pending', // el mismo, como lo escribe company-config.json
])

/**
 * Estados que no merecen círculo propio pero sí explicación.
 *
 * Se enganchan como rama del mayor anterior que exista en el catálogo, igual
 * que un sub-estado: el círculo del padre se enciende, el badge lleva su nombre
 * y la línea de tiempo muestra su descripción. Es distinto de `TRACK_EXCLUDED`,
 * que los borra del todo y deja la tarea en "Estado no disponible".
 *
 * `ot_sent_sap` ("7. Enviada a SAP") es trámite administrativo: a la estación
 * no le dice nada como etapa propia, pero una OT parada ahí tiene que poder
 * explicarse.
 */
const BRANCH_OF_PREVIOUS = new Set(['ot_sent_sap'])

/**
 * Tareas que no se listan (corrección §5.4). Se excluye, no se incluye: así un
 * estado nuevo creado en Cotalker aparece por defecto en vez de desaparecer
 * en silencio.
 */
export const LIST_EXCLUDED_INCIDENTS = new Set(['incidents_closed', 'incidents_rejected'])
export const LIST_EXCLUDED_WORK_ORDERS = new Set([
  'ot_cancelled',
  'ot_checklist_pending',
  'ot_checklists_pending',
])

/**
 * Desempate: el mayor 0 de OT tiene dos miembros sin ordinal menor
 * (`ot_start_date_review` y `ot_future_tasks`). Este mapa elige cuál representa
 * al círculo y permite reescribir la etiqueta para el usuario final.
 */
export const TRACK_OVERRIDES: Record<string, { code?: string; label?: string }> = {
  'ot:0': { code: 'ot_start_date_review', label: 'En revisión fecha inicio' },
}

const ICONS: Record<string, string> = {
  incidents_review_zone_leader: 'eye',
  incidents_review_mantti: 'clipboard',
  incidents_process: 'wrench',
  incidents_taskcompleted: 'check',
  incidents_closed: 'flag',
  ot_start_date_review: 'calendar',
  ot_assigned: 'hardhat',
  ot_execution: 'wrench',
  ot_review: 'search',
  ot_pre_closed: 'box',
  ot_preliminary_closed: 'check',
  ot_closed_end: 'lock',
}
const ICON_FALLBACK = ['eye', 'clipboard', 'wrench', 'search', 'box', 'check', 'lock', 'upload']

export function parseOrdinal(display: string): { major: number; minor: number | null } | null {
  const m = ORDINAL.exec(display ?? '')
  if (!m || m[1] === undefined) return null
  return { major: Number(m[1]), minor: m[2] === undefined ? null : Number(m[2]) }
}

/** Quita el prefijo ordinal para mostrar la etiqueta limpia. */
export function stripOrdinal(display: string): string {
  return (display ?? '').replace(ORDINAL, '').trim()
}

export function buildTrack(entries: CatalogEntry[], kind: 'inc' | 'ot'): Track {
  type Parsed = CatalogEntry & { major: number; minor: number | null }

  // Se llena con TODAS las entradas, incluidas las excluidas del track: el id
  // que hace falta para filtrar el listado es justamente el de una excluida.
  const smStateByCode: Record<string, ObjectId> = {}
  for (const e of entries) if (e.smStateId) smStateByCode[e.code] = e.smStateId

  const parsed: Parsed[] = []
  for (const e of entries) {
    if (TRACK_EXCLUDED.has(e.code)) continue
    const o = parseOrdinal(e.display)
    if (!o) continue // sin prefijo ordinal no hay lugar en el track
    parsed.push({ ...e, major: o.major, minor: o.minor })
  }

  /*
   * Los estados de `BRANCH_OF_PREVIOUS` se mudan al mayor anterior que exista.
   * No se escribe el número a mano —hoy `ot_sent_sap` es 7 y el anterior es 6—
   * porque el catálogo lo maneja Cotalker: si mañana se reordena el flujo, el
   * enganche sigue cayendo donde corresponde en vez de quedar apuntando al aire.
   *
   * El menor alto lo saca de la elección de representante del círculo: el paso
   * lo sigue representando el estado raíz del mayor de destino.
   */
  const BRANCH_MINOR = 999
  const majorsPresent = [...new Set(parsed.map((p) => p.major))].sort((a, b) => a - b)
  for (const p of parsed) {
    if (!BRANCH_OF_PREVIOUS.has(p.code)) continue
    const previous = majorsPresent.filter((m) => m < p.major).pop()
    if (previous === undefined) continue // no hay dónde engancharlo: se deja como paso
    p.major = previous
    p.minor = BRANCH_MINOR
  }

  const byMajor = new Map<number, Parsed[]>()
  for (const p of parsed) {
    const list = byMajor.get(p.major)
    if (list) list.push(p)
    else byMajor.set(p.major, [p])
  }

  const majors = [...byMajor.keys()].sort((a, b) => a - b)
  const steps: TrackStep[] = []
  const branches: Record<string, Branch> = {}
  const byPropertyId: Record<ObjectId, string> = {}

  majors.forEach((major, index) => {
    const members = byMajor.get(major) ?? []
    for (const m of members) byPropertyId[m.propertyId] = m.code

    const override = TRACK_OVERRIDES[`${kind}:${major}`]
    const roots = members.filter((m) => m.minor === null)

    // El representante del círculo: el que fija la configuración, si está;
    // si no, el único sin ordinal menor; y si no hay ninguno, el menor sub-estado.
    const rep =
      (override?.code && members.find((m) => m.code === override.code)) ||
      roots[0] ||
      members.slice().sort((a, b) => (a.minor ?? 0) - (b.minor ?? 0))[0]

    if (!rep) return

    steps.push({
      step: index,
      code: rep.code,
      label: override?.label ?? stripOrdinal(rep.display),
      icon: ICONS[rep.code] ?? ICON_FALLBACK[index % ICON_FALLBACK.length] ?? 'check',
    })

    // Todo lo demás del mismo mayor es rama: enciende este círculo y muestra
    // su nombre completo en un badge. Incluye a los otros miembros sin ordinal
    // menor, como `ot_future_tasks`.
    for (const m of members) {
      if (m.code === rep.code) continue
      branches[m.code] = { code: m.code, parent: index, display: stripOrdinal(m.display) }
    }
  })

  return { steps, branches, byPropertyId, smStateByCode }
}

export const UNKNOWN_STATE: ResolvedState = {
  step: null,
  label: 'Estado no disponible',
  code: null,
  isBranch: false,
  branchLabel: null,
  branchCode: null,
  unknown: true,
}

/**
 * El estado desconocido **conserva el código** cuando lo hay.
 *
 * Es lo que separa "no sé qué estado es esto" de "sé cuál es y no tiene lugar
 * en el track". Las listas de exclusión del listado filtran por código, así que
 * devolver `code: null` acá las volvía código muerto para todo estado que
 * además estuviera fuera del track: una incidencia `incidents_rejected` pasaba
 * el filtro y se listaba como "Estado no disponible".
 */
const unknownWithCode = (code: string): ResolvedState => ({ ...UNKNOWN_STATE, code })

export function resolveState(code: string | null | undefined, track: Track): ResolvedState {
  if (!code) return UNKNOWN_STATE

  const direct = track.steps.find((s) => s.code === code)
  if (direct) {
    return {
      step: direct.step,
      label: direct.label,
      code,
      isBranch: false,
      branchLabel: null,
      branchCode: null,
      unknown: false,
    }
  }

  const br = track.branches[code]
  if (br) {
    const parent = track.steps.find((s) => s.step === br.parent)
    if (parent) {
      return {
        step: br.parent,
        label: parent.label,
        code,
        isBranch: true,
        branchLabel: br.display,
        branchCode: code,
        unknown: false,
      }
    }
  }

  // Estados legacy dados de baja: no están en el catálogo, que solo devuelve
  // los activos (corrección §2.1). La UI degrada a pipeline gris.
  return unknownWithCode(code)
}

/** spec §4: percent = round(step / lastStep * 100). */
export function progressOf(step: number | null, track: Track): Progress {
  const total = track.steps.length
  const last = Math.max(1, total - 1)
  if (step === null) return { step: null, totalSteps: total, percent: 0 }
  return { step, totalSteps: total, percent: Math.round((step / last) * 100) }
}

/** Tono del badge según dónde cae el estado en el camino. */
export function stateTone(state: ResolvedState): 'slate' | 'amber' | 'blue' | 'green' {
  if (state.unknown || state.step === null) return 'slate'
  if (state.step <= 1) return 'slate'
  if (state.step === 2) return 'amber'
  if (state.step === 3) return 'blue'
  return 'green'
}
